import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Check, Leaf, Loader2, Mail } from "lucide-react";
import { subscribeEmail } from "@/lib/subscribers.functions";
import { getAttribution } from "@/lib/attribution";
import { track } from "@/lib/track";

type Props = {
  /** Where the signup happened, e.g. "homepage" or "footer". */
  source: string;
  variant?: "section" | "compact";
};

/**
 * Free newsletter signup — a softer secondary conversion for visitors who
 * are not ready to join the membership. Never redirects to checkout.
 */
export function NewsletterSignup({ source, variant = "section" }: Props) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [message, setMessage] = useState("");
  const [heading, setHeading] = useState("Check your inbox 📩");

  const rootRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);
  const viewedRef = useRef(false);
  const subscribe = useServerFn(subscribeEmail);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !viewedRef.current) {
            viewedRef.current = true;
            track("newsletter_cta_viewed", { source, variant });
          }
        }
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [source, variant]);

  function onFocus() {
    if (startedRef.current) return;
    startedRef.current = true;
    track("newsletter_signup_started", { source, variant });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    setStatus("loading");
    try {
      const attr = getAttribution();
      const res = (await subscribe({
        data: {
          email: value,
          source,
          // Explicit consent: the visitor typed their address and pressed
          // "Subscribe to newsletter" under the consent wording below.
          marketing_consent: true,
          utm_source: attr?.utm_source ?? null,
          utm_medium: attr?.utm_medium ?? null,
          utm_campaign: attr?.utm_campaign ?? null,
          referrer: attr?.referrer ?? null,
          landing_path: attr?.landing_path ?? null,
          platform: attr?.platform ?? null,
        },
      })) as { ok: boolean; duplicate?: boolean; pending?: boolean };

      if (res?.duplicate) {
        track("newsletter_duplicate", { source, variant });
        setMessage("You're already on the list 🌱 — nothing more to do.");
        setHeading("You're already subscribed 🌱");
      } else {
        track("newsletter_confirmation_sent", { source, variant });
        setMessage(
          "We've sent a confirmation email — please open it and tap the link to finish subscribing. You're not on the list until you do.",
        );
        setHeading("Check your inbox 📩");
      }

      setStatus("done");
      setEmail("");
    } catch {
      setStatus("error");
      setMessage("Something went wrong. Please try again in a moment.");
    }
  }

  const done = status === "done";

  if (variant === "compact") {
    return (
      <div ref={rootRef} className="w-full">
        {done ? (
          <p className="flex items-start gap-2 text-sm text-foreground">
            <Check className="mt-0.5 h-4 w-4 flex-none text-[var(--gold-deep)]" />
            <span>{message || "Please confirm your email to finish subscribing."}</span>
          </p>
        ) : (
          <form onSubmit={onSubmit} className="space-y-2">
            <label htmlFor="footer-newsletter-email" className="block text-sm font-semibold text-foreground">
              Stay connected with Recovery Trauma™ 🌱
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                id="footer-newsletter-email"
                type="email"
                required
                maxLength={255}
                value={email}
                onFocus={onFocus}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="min-h-11 w-full min-w-0 rounded-full border border-gold/35 bg-background px-4 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-5 text-sm font-semibold text-foreground shadow-[var(--shadow-gold)] transition hover:opacity-95 disabled:opacity-60"
              >
                {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Subscribe
              </button>
            </div>
            {status === "error" && <p className="text-xs text-destructive">{message}</p>}
            <p className="text-xs text-foreground/55">No spam. Unsubscribe anytime.</p>
          </form>
        )}
      </div>
    );
  }

  return (
    <section aria-labelledby="newsletter-heading" className="mx-auto w-full max-w-3xl px-4">
      <div
        ref={rootRef}
        className="rounded-3xl border border-gold/30 bg-cream/60 p-4 text-center shadow-[0_20px_60px_-40px_rgba(0,0,0,0.35)] dark:bg-cream/10 sm:p-6"
      >
        <span className="mx-auto grid h-10 w-10 place-items-center rounded-2xl bg-[var(--gradient-gold)] text-foreground">
          {done ? <Leaf className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
        </span>

        {done ? (
          <>
            <h2 id="newsletter-heading" className="mt-3 font-serif text-xl text-foreground sm:text-2xl">
              {heading}
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
              {message ||
                "Please open the confirmation email and tap the link to finish subscribing."}
            </p>

          </>
        ) : (
          <>
            <h2 id="newsletter-heading" className="mt-3 font-serif text-xl text-foreground sm:text-2xl">
              Stay connected with Recovery Trauma™ 🌱
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
              Gentle recovery insights, practical tools, new resources and upcoming workshops —
              delivered to your inbox.
            </p>
            <form onSubmit={onSubmit} className="mx-auto mt-4 flex max-w-lg flex-col gap-2.5 sm:flex-row">
              <label htmlFor="newsletter-email" className="sr-only">
                Email address
              </label>
              <input
                id="newsletter-email"
                type="email"
                required
                maxLength={255}
                value={email}
                onFocus={onFocus}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="min-h-11 w-full min-w-0 rounded-full border border-gold/35 bg-background px-4 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-full bg-[var(--gradient-gold)] px-5 text-sm font-semibold text-foreground shadow-[var(--shadow-gold)] transition hover:opacity-95 disabled:opacity-60"
              >
                {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Subscribe to newsletter
              </button>
            </form>
            {status === "error" && <p className="mt-2 text-xs text-destructive">{message}</p>}
            <p className="mt-2 text-xs text-muted-foreground">No spam. Unsubscribe anytime.</p>
          </>
        )}
      </div>
    </section>
  );
}
